import { CatalogType } from "./CatalogType";

export enum RFQStatus {
  NEW = "NEW",
  DRAFT = "DRAFT",
  OPEN = "OPEN",
  ORDER = "ORDER",
  IN_PROGRESS = "IN_PROGRESS",
  QUOTED = "QUOTED",
  COMPLETED = "COMPLETED",
  PROBLEM = "PROBLEM",
  CANCELLED = "CANCELLED",
  CLOSED = "CLOSED",
}

/** Default selectable statuses for an approved RFQ (excludes NEW). */
export const DEFAULT_RFQ_STATUS_OPTIONS: RFQStatus[] = [
  RFQStatus.DRAFT,
  RFQStatus.OPEN,
  RFQStatus.ORDER,
  RFQStatus.IN_PROGRESS,
  RFQStatus.QUOTED,
  RFQStatus.COMPLETED,
  RFQStatus.PROBLEM,
  RFQStatus.CANCELLED,
  RFQStatus.CLOSED,
];

/** Statuses where an RFQ is parked rather than being worked; left out of speed totals. */
export const PARKED_RFQ_STATUSES = new Set<string>([
  RFQStatus.COMPLETED,
  RFQStatus.CANCELLED,
  RFQStatus.CLOSED,
]);

const DEFAULT_STATUS_LABELS: Record<string, string> = {
  [RFQStatus.NEW]: "New",
  [RFQStatus.DRAFT]: "Draft",
  [RFQStatus.OPEN]: "Open",
  [RFQStatus.ORDER]: "Order",
  [RFQStatus.IN_PROGRESS]: "In Progress",
  [RFQStatus.QUOTED]: "Quoted",
  [RFQStatus.COMPLETED]: "Completed",
  [RFQStatus.PROBLEM]: "Problem",
  [RFQStatus.CANCELLED]: "Cancelled",
  [RFQStatus.CLOSED]: "Closed",
  HOT: "Hot",
  PO: "PO",
};

export const getRFQStatusLabel = (
  status: string,
  catalog?: { value: string; label: string }[]
) => {
  const fromCatalog = catalog
    ?.find((t) => t.value === status)
    ?.label?.trim();
  if (fromCatalog) {
    return fromCatalog;
  }
  return DEFAULT_STATUS_LABELS[status] ?? status;
};

export type RfqStatusChipColors = {
  bg: string;
  fg: string;
  border: string;
};

export type RfqStatusColorCatalogEntry = Pick<
  CatalogType,
  "value" | "colorBg" | "colorFg" | "colorBorder"
>;

/** Normalize #RGB, #RRGGBB, or rgb(r,g,b) to uppercase #RRGGBB. */
export function normalizeHexColor(raw: unknown): string | null {
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return `#${trimmed}`.toUpperCase();
  }
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  const rgb = /^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(
    trimmed
  );
  if (rgb) {
    const clamp = (n: number) => Math.max(0, Math.min(255, n));
    const toHex = (n: number) => clamp(n).toString(16).padStart(2, "0");
    return `#${toHex(parseInt(rgb[1], 10))}${toHex(parseInt(rgb[2], 10))}${toHex(parseInt(rgb[3], 10))}`.toUpperCase();
  }

  return null;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = normalizeHexColor(hex) ?? "#000000";
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const toHex = (n: number) => clamp(n).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function contrastTextColor(bgHex: string): string {
  const { r, g, b } = hexToRgb(bgHex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#212121" : "#FFFFFF";
}

function darkenHexColor(bgHex: string, amount = 0.12): string {
  const { r, g, b } = hexToRgb(bgHex);
  const factor = 1 - amount;
  return rgbToHex(r * factor, g * factor, b * factor);
}

const DEFAULT_STATUS_COLORS: Record<string, RfqStatusChipColors> = {
  HOT: { bg: "#EF9A9A", fg: "#B71C1C", border: "#E57373" },
  PO: { bg: "#B3E5FC", fg: "#01579B", border: "#4FC3F7" },
  OPEN: { bg: "#FFB74D", fg: "#E65100", border: "#FB8C00" },
  ORDER: { bg: "#81D4FA", fg: "#01579B", border: "#039BE5" },
  IN_PROGRESS: {
    bg: "#FF69B4",
    fg: "#FFFFFF",
    border: "#FF1493",
  },
  QUOTED: { bg: "#1A237E", fg: "#E8EAF6", border: "#283593" },
  CANCELLED: {
    bg: "#7B1FA2",
    fg: "#F3E5F5",
    border: "#9C27B0",
  },
  CLOSED: { bg: "#7B1FA2", fg: "#F3E5F5", border: "#9C27B0" },
  PROBLEM: { bg: "#7B1FA2", fg: "#F3E5F5", border: "#9C27B0" },
  COMPLETED: { bg: "#C8E6C9", fg: "#1B5E20", border: "#81C784" },
  NEW: { bg: "#FFF9C4", fg: "#F57F17", border: "#FBC02D" },
  DRAFT: { bg: "#ECEFF1", fg: "#37474F", border: "#B0BEC5" },
};

function resolveCatalogStatusColors(
  entry: RfqStatusColorCatalogEntry
): RfqStatusChipColors | null {
  const bg = entry.colorBg ? normalizeHexColor(entry.colorBg) : null;
  if (!bg) {
    return null;
  }
  const fgRaw = entry.colorFg ? normalizeHexColor(entry.colorFg) : null;
  const borderRaw = entry.colorBorder ? normalizeHexColor(entry.colorBorder) : null;
  return {
    bg,
    fg: fgRaw ?? contrastTextColor(bg),
    border: borderRaw ?? darkenHexColor(bg),
  };
}

/** Chip colors for work-queue calendar and status selectors. */
export function getRfqStatusColor(
  status: string,
  catalog?: RfqStatusColorCatalogEntry[]
): RfqStatusChipColors {
  const entry = catalog?.find((t) => t.value === status);
  if (entry) {
    const fromCatalog = resolveCatalogStatusColors(entry);
    if (fromCatalog) {
      return fromCatalog;
    }
  }

  const preset = DEFAULT_STATUS_COLORS[status];
  if (preset) {
    return preset;
  }

  return { bg: "#E0F2F1", fg: "#004D40", border: "#80CBC4" };
}
